/* eslint-disable max-lines */
import {gql} from "@apollo/client";
import {AdaptiveInput} from "../../schema";
import {SessionResultFeedback} from "../../types";

export const sessionStartMutation = gql`
  mutation PlayerSessionStartMutation($challengeId: ID!, $preview: Boolean!, $externalUserId: String, $utm: UTM) {
    sessionStartResult: educationSessionStart(
      challengeId: $challengeId
      preview: $preview
      externalUserId: $externalUserId
      utm: $utm
    ) {
      sessionId
      recognitionEngine
    }
  }
`;

export const sessionSkipMutation = gql`
  mutation PlayerSessionSkipMutation($actionId: ID!, $level: Int!, $sessionId: ID!) {
    ok: educationSessionSkip(actionId: $actionId, level: $level, sessionId: $sessionId)
  }
`;

export const sessionUseHintMutation = gql`
  mutation PlayerSessionUseHintMutation($actionId: ID!, $level: Int!, $sessionId: ID!) {
    ok: educationSessionUseHint(actionId: $actionId, level: $level, sessionId: $sessionId)
  }
`;

export const sessionStartLevelMutation = gql`
  mutation PlayerSessionStartLevelMutation($level: Int!, $sessionId: ID!) {
    ok: educationSessionStartLevel(level: $level, sessionId: $sessionId)
  }
`;

export const sessionPassStreamUserInputMutation = gql`
  mutation PlayerSessionPassStreamMutation(
    $uuid: ID!
    $actionId: ID!
    $level: Int!
    $sessionId: ID!
    $audioUploadId: ID
    $videoUploadId: ID
    $recognizedText: String
    $duration: Float
    $sttError: Boolean
  ) {
    result: educationSessionPassStream(
      uuid: $uuid
      actionId: $actionId
      level: $level
      sessionId: $sessionId
      audioUploadId: $audioUploadId
      videoUploadId: $videoUploadId
      recognizedText: $recognizedText
      duration: $duration
      sttError: $sttError
    ) {
      passed
      branchId
      recognizedText
      scenarioId
      interrupt
      errorReason
      inputId
      waitForFeedback
    }
  }
`;

export const sessionSelectInputMutation = gql`
  mutation PlayerSessionSelectInputMutation($actionId: ID!, $level: Int!, $sessionId: ID!, $index: Int) {
    result: educationSessionSelectInput(actionId: $actionId, level: $level, sessionId: $sessionId, index: $index) {
      passed
      branchId
      scenarioId
      interrupt
      errorReason
      inputId
    }
  }
`;

export const sessionFinish = gql`
  mutation PlayerSessionFinish($sessionId: ID!) {
    result: educationSessionFinish(sessionId: $sessionId) {
      sessionId
      passed
      score
      time
    }
  }
`;

export const sessionSubmitFeedback = gql`
  mutation PlayerSessionSubmitFeedback(
    $data: DetailedSessionFeedbackInput
    $score: Int
    $message: String
    $contacts: String
    $sessionId: ID!
  ) {
    ok: educationSessionSubmitFeedback(
      data: $data
      score: $score
      message: $message
      contacts: $contacts
      sessionId: $sessionId
    )
  }
`;

export const sessionResumeMutation = gql`
  mutation PlayerSessionResume($challengeId: ID!, $externalUserId: ID) {
    progress: educationSessionResume(challengeId: $challengeId, externalUserId: $externalUserId) {
      sessionId
      level
      openedActions
      exposedActions
      lastPrevScenarioActionIds
      prevScenarioIds
      lastScenarioId
      lastScenarioAction
      lastMainScenarioAction
    }
  }
`;

export const scenarioFinishMutation = gql`
  mutation PlayerSessionFinishScenario($sessionId: ID!, $level: Int!, $scenarioIds: [ID!]!, $actionId: ID) {
    ok: educationSessionFinishScenario(
      sessionId: $sessionId
      level: $level
      scenarioIds: $scenarioIds
      actionId: $actionId
    )
  }
`;

export const sessionMultipleSelectMutation = gql`
  mutation SessionMultipleSelectMutation(
    $sessionId: ID!
    $level: Int!
    $actionId: ID!
    $chosenBranches: [ID!]!
    $chosenBranchesData: [[String!]!]!
  ) {
    result: educationSessionMultipleSelectInput(
      sessionId: $sessionId
      level: $level
      actionId: $actionId
      chosenBranches: $chosenBranches
      chosenBranchesData: $chosenBranchesData
    ) {
      passed
      interrupt
      errorReason
    }
  }
`;

export const sessionCheckSelectOrVoiceInputMutation = gql`
  mutation PlayerSessionCheckSelectOrVoiceInputMutation(
    $actionId: ID!
    $level: Int!
    $sessionId: ID!
    $index: Int
    $audioUploadId: ID
    $duration: Float
    $recognizedText: String
    $sttError: Boolean
    $uuid: ID
    $videoUploadId: ID
  ) {
    result: educationSessionCheckSelectOrVoiceInput(
      actionId: $actionId
      level: $level
      sessionId: $sessionId
      index: $index
      audioUploadId: $audioUploadId
      duration: $duration
      recognizedText: $recognizedText
      sttError: $sttError
      uuid: $uuid
      videoUploadId: $videoUploadId
    ) {
      passed
      branchId
      scenarioId
      interrupt
      errorReason
    }
  }
`;

export type AdaptiveInputQueryData = {
  input: AdaptiveInput;
};

export const getAdaptiveInputQuery = gql`
  query PlayerGetAdaptiveInputQuery($sessionId: ID!, $inputId: ID!) {
    input: getAdaptiveInput(sessionId: $sessionId, inputId: $inputId) {
      id
      uuid
      isClosed
      isError
      responses {
        index
        audioUrl
        text
        id
      }
    }
  }
`;

export type PassAdaptiveInputMutationData = {
  result: boolean | null;
};

export const passAdaptiveInputMutation = gql`
  mutation PlayerPassAdaptiveInputMutation(
    $sessionId: ID!
    $actionId: ID!
    $adaptiveInputUuid: String!
    $recognizedText: String
    $audioUuid: String
  ) {
    result: educationSessionPassAdaptiveInput(
      sessionId: $sessionId
      actionId: $actionId
      adaptiveInputUuid: $adaptiveInputUuid
      recognizedText: $recognizedText
      audioUuid: $audioUuid
    )
  }
`;

export type SessionResultFeedbackData = {
  result: {
    resultFeedback: SessionResultFeedback | null;
  } | null;
};

export const getSessionResultFeedbackQuery = gql`
  query GetSessionResultFeedback($sessionId: ID!) {
    result: getSessionResult(sessionId: $sessionId) {
      resultFeedback {
        text
        audioUrl
        feedbackStatus
      }
    }
  }
`;

export const sessionStartRpgPracticeMutation = gql`
  mutation PlayerSessionStartRpgPracticeMutation($sessionId: ID!) {
    result: educationStartRpgPractice(sessionId: $sessionId) {
      isStarted
      avatarText
      avatarAudio
    }
  }
`;
